import { supabase } from "@/integrations/supabase/client";

// Facebook Pixel tracking
let cachedPixelId: string | null = null;
let fbqReady = false;
const eventQueue: Array<{ name: string; data?: any; eventId: string }> = [];

// Unified event deduplication system
const sentEvents = new Map<string, number>(); // eventKey -> timestamp
const DEDUP_WINDOW = 5 * 60 * 1000; // 5 minutes

// Helper to create SHA256 hash
async function sha256(text: string): Promise<string> {
  if (!text) return '';
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(text.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

export function flushQueuedEvents() {
  try {
    if (typeof window !== 'undefined' && (window as any).fbq && eventQueue.length) {
      const q = eventQueue.splice(0);
      q.forEach((e) => (window as any).fbq('track', e.name, e.data, { eventID: e.eventId }));
      fbqReady = true;
      console.log(`Flushed ${q.length} queued FB events`);
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('flushQueuedEvents error', e);
  }
}

async function ensurePixelId(): Promise<string | null> {
  if (cachedPixelId) return cachedPixelId;
  try {
    const { data } = await supabase.functions.invoke('get-active-pixel');
    cachedPixelId = data?.pixel_id ?? null;
    return cachedPixelId;
  } catch {
    return null;
  }
}

export const trackEvent = async (eventName: string, eventData?: any) => {
  try {
    // Generate a stable eventId used for browser+server deduplication
    const eventId = (eventData && (eventData.event_id || eventData.eventId))
      || (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

    // Unified deduplication check - prevent duplicate events
    const eventKey = `${eventName}:${eventId}`;
    const now = Date.now();

    // Check in-memory Map
    const lastSent = sentEvents.get(eventKey);
    if (lastSent && (now - lastSent) < DEDUP_WINDOW) {
      console.log(`🚫 Event already sent: ${eventName} (${eventId}) - ${Math.round((now - lastSent) / 1000)}s ago`);
      return;
    }

    // Check sessionStorage for cross-tab protection
    try {
      if (typeof sessionStorage !== 'undefined') {
        const storageKey = `fb_event:${eventKey}`;
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          const storedTime = parseInt(stored);
          if (!isNaN(storedTime) && (now - storedTime) < DEDUP_WINDOW) {
            console.log(`🚫 Event already sent (storage): ${eventName} (${eventId})`);
            return;
          }
        }
        sessionStorage.setItem(storageKey, now.toString());
      }
    } catch { }

    // Mark event as sent
    sentEvents.set(eventKey, now);

    // Clean old entries from Map (keep only recent 5 minutes)
    for (const [key, timestamp] of sentEvents.entries()) {
      if (now - timestamp > DEDUP_WINDOW) {
        sentEvents.delete(key);
      }
    }

    // Attach experiment info (if any)
    let expPayload: Record<string, any> = {};
    try {
      if (typeof window !== 'undefined') {
        const raw = localStorage.getItem('exp:lp_test_v1');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.variant) {
            expPayload = { exp_name: parsed.name ?? 'lp_test_v1', exp_variant: parsed.variant };
          }
        }
      }
    } catch { }

    const payload = {
      ...eventData,
      ...expPayload,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    };

    // Save to analytics_events table for tracking - defer to not block critical path
    if ('requestIdleCallback' in window) {
      requestIdleCallback(async () => {
        try {
          await supabase.from('analytics_events').insert({
            event_name: eventName,
            event_data: payload,
          });
        } catch (e) {
          if (import.meta.env.DEV) console.warn('Failed to save analytics event', e);
        }
      });
    } else {
      // Fallback - still defer but use setTimeout
      setTimeout(async () => {
        try {
          await supabase.from('analytics_events').insert({
            event_name: eventName,
            event_data: payload,
          });
        } catch (e) {
          if (import.meta.env.DEV) console.warn('Failed to save analytics event', e);
        }
      }, 100);
    }

    // Client-side Facebook Pixel (queue if not ready)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', eventName, payload, { eventID: eventId });
      fbqReady = true;
    } else {
      eventQueue.push({ name: eventName, data: payload, eventId });
    }

    // Prepare enhanced user_data with hashing
    const prepareUserData = async () => {
      const userData: any = {
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        fbp: typeof document !== 'undefined' ? getCookie('_fbp') : '',
        fbc: typeof document !== 'undefined' ? getCookie('_fbc') : '',
      };

      // Add hashed email/phone if available in payload
      if (payload.customer_email) {
        userData.em = await sha256(payload.customer_email);
      }
      if (payload.customer_phone) {
        userData.ph = await sha256(payload.customer_phone);
      }
      if (payload.customer_name) {
        const names = payload.customer_name.split(' ');
        if (names[0]) userData.fn = await sha256(names[0]);
        if (names[names.length - 1]) userData.ln = await sha256(names[names.length - 1]);
      }

      return userData;
    };

    // Server-side tracking via backend function (Conversion API) - defer to not block render
    const sendServerEvent = async () => {
      try {
        const pixelId = await ensurePixelId();
        const userData = await prepareUserData();

        const response = await supabase.functions.invoke('facebook-pixel-event', {
          body: {
            eventName,
            pixelId: pixelId ?? undefined,
            eventData: {
              ...payload,
              event_id: eventId,
              event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
              user_data: userData,
            },
          },
        });

        console.log(`✅ Event sent: ${eventName} (${eventId})`, response);
      } catch (error) {
        console.error(`❌ Failed to send event: ${eventName}`, error);
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => sendServerEvent());
    } else {
      setTimeout(() => sendServerEvent(), 100);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error tracking event:', error);
    }
  }
};

// Track PageView event
export const trackPageView = (pageName: string, pageData?: any) => {
  trackEvent('PageView', {
    page: pageName,
    ...pageData,
  });
};

// Helper to get cookie value
function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
  return '';
}

// Track ViewContent event
export const trackViewContent = (productName: string, price: number) => {
  trackEvent('ViewContent', {
    content_name: productName,
    content_type: 'product',
    value: price,
    currency: 'EGP',
  });
};

// Track InitiateCheckout event
export const trackInitiateCheckout = (productName: string, price: number) => {
  trackEvent('InitiateCheckout', {
    content_name: productName,
    content_type: 'product',
    value: price,
    currency: 'EGP',
  });
};

// Track Purchase event with enhanced data
export const trackPurchase = async (orderId: string, price: number, customerData?: {
  email?: string;
  phone?: string;
  name?: string;
  clickId?: string; // e.g., fbclid
}) => {
  console.log(`🛒 Tracking Purchase: Order ${orderId}, Value: ${price} EGP`);

  // Build custom_data to carry click identifiers
  const custom_data: Record<string, any> = {};
  if (customerData?.clickId) {
    custom_data.click_id = customerData.clickId;
    custom_data.fbclid = customerData.clickId;
  }

  await trackEvent('Purchase', {
    value: price,
    currency: 'EGP',
    content_ids: [orderId],
    content_type: 'product',
    event_id: orderId, // Use orderId as eventId for deduplication
    customer_email: customerData?.email,
    customer_phone: customerData?.phone,
    customer_name: customerData?.name,
    custom_data,
  });

  // Track A/B test conversion if applicable
  const sessionId = localStorage.getItem('ab_session_id');
  if (sessionId) {
    // Find active experiment assignment
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('ab_assignment_')) {
        try {
          const assignment = JSON.parse(localStorage.getItem(key) || '{}');
          if (assignment.sessionId === sessionId) {
            await supabase.from('experiment_results').insert({
              experiment_id: assignment.experimentId,
              variant_id: assignment.variantId,
              session_id: sessionId,
              event_type: 'purchase',
              event_data: {
                order_id: orderId,
                value: price,
                timestamp: new Date().toISOString()
              }
            });
          }
        } catch (e) {
          console.error('Error tracking A/B conversion:', e);
        }
      }
    }
  }
};

// Track Quiz Completion
export const trackQuizCompletion = (quizTitle: string, answers: any[], result?: string) => {
  trackEvent('QuizCompletion', {
    content_name: quizTitle,
    content_type: 'quiz',
    answers: answers,
    result: result,
  });
};

// Track Spin to Win Result
export const trackSpinWin = (wheelTitle: string, prize: string, prizeValue?: string) => {
  trackEvent('SpinToWin', {
    content_name: wheelTitle,
    content_type: 'game',
    prize: prize,
    value: prizeValue,
  });
};

// Track Video Bubble Interaction
export const trackVideoInteraction = (videoMessage: string, action: 'expand' | 'click_cta' | 'mute' | 'unmute') => {
  trackEvent('VideoInteraction', {
    content_name: videoMessage,
    content_type: 'video_bubble',
    action: action,
  });
};

// Track Sales Notification Click
export const trackNotificationClick = (productName: string) => {
  trackEvent('NotificationClick', {
    content_name: productName,
    content_type: 'sales_notification',
  });
};